"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Info } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const services = [{
  category: "Clinical Review",
  title: "Diagnosis Interpretation & Staging",
  shortDesc: "Gain a clear understanding of your diagnosis, staging, and overall risk.",
  detailDesc: "I provide a comprehensive review of your PSA history, biopsy results, MRI findings, and pathology reports. I translate complex clinical data into clear, practical insights so you fully understand where you stand and what it means for your next steps.",
  image: "https://trusted-amoeba.10web.cloud/wp-content/uploads/2026/04/medical-record-scaled.jpg",
  imageAlt: "Detailed genomic test result report showing personalized cancer risk markers and analysis",
  imageClassName: "absolute inset-0 bg-primary/10 mix-blend-multiply transition-opacity duration-500 group-hover:opacity-0 z-10 !bg-[url(https://trusted-amoeba.10web.cloud/wp-content/uploads/2026/04/medical-record-scaled.jpg)]"
}, {
  category: "Treatment Planning",
  title: "Treatment Options Exploration",
  shortDesc: "Understand your options and choose a path that aligns with your goals.",
  detailDesc: "I walk you through all appropriate options including active surveillance, surgery, radiation, focal therapy, and systemic treatments. I help you understand tradeoffs, side effects, and quality of life implications so you can make a confident, informed decision.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605152/img/treatment_options_consultation.png",
  imageAlt: "Patient and navigator discussing treatment plans over a desk"
}, {
  category: "Genomics",
  title: "Genomic & Germline Testing Guidance",
  shortDesc: "Understand when advanced testing is useful and how to interpret the results.",
  detailDesc: "I provide guidance on blood, urine, and tissue based genomic tests, as well as germline genetic testing. Although guidelines now support broader access, real world studies show that many eligible patients still do not receive testing, and a meaningful percentage of actionable findings are missed. With deep experience in genomic and germline testing, I help you determine when testing is appropriate and clearly explain what the results mean for your risk, prognosis, and treatment decisions.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605152/img/genomic_testing_analysis.png",
  imageAlt: "Modern laboratory setting representing genomic testing precision"
}, {
  category: "Family Support",
  title: "Caregiver & Family Guidance",
  shortDesc: "Support for spouses and families navigating this journey alongside you.",
  detailDesc: "I work directly with spouses, partners, and family members to help them understand the diagnosis, treatment options, and decision making process. This ensures everyone is aligned, informed, and able to provide meaningful support.",
  image: "https://trusted-amoeba.10web.cloud/wp-content/uploads/2026/07/shutterstock_2764785487-scaled.jpg",
  imageAlt: "Supportive consultation with a patient and their spouse"
}];
function ServiceBlock({
  item,
  index
}: {
  item: typeof services[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} data-index={index} className={cn("flex flex-col gap-8 lg:gap-16 items-center transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12", isEven ? "lg:flex-row" : "lg:flex-row-reverse")}>
      {}
      <div className="w-full lg:w-1/2 relative group overflow-hidden rounded-3xl shadow-xl">
        <div className={`${item.imageClassName || ""} absolute inset-0 bg-primary/10 mix-blend-multiply transition-opacity duration-500 group-hover:opacity-0 z-10`} />
        <img src={item.image} alt={item.imageAlt} className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105" />
      </div>

      {}
      <div className="w-full lg:w-1/2 flex flex-col justify-center group cursor-default">
        <Badge variant="secondary" className="mb-6 w-fit text-sm px-4 py-1 bg-secondary/10 text-secondary-foreground hover:bg-secondary/20 transition-colors">
          {item.category}
        </Badge>
        
        <h3 className="text-3xl md:text-4xl font-serif text-foreground mb-4 leading-tight">
          {item.title}
        </h3>
        
        <p className="text-lg text-muted-foreground leading-relaxed mb-2">
          {item.shortDesc}
        </p>

        {}
        <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-[grid-template-rows] duration-500 ease-in-out">
          <div className="overflow-hidden">
            <div className="border-t border-border/50 pt-4 flex items-start gap-3 mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
              <Info className="w-5 h-5 text-primary shrink-0 mt-1" />
              <p className="text-base text-foreground/80 leading-relaxed">
                {item.detailDesc}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
export default function ComprehensiveNavigationServices() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <section id="comprehensive-navigation-services" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 overflow-hidden">
      <div className="container mx-auto px-6 md:px-8 max-w-7xl">
        
        {}
        <div ref={headerRef} className={cn("max-w-3xl mb-12 lg:mb-16 transition-all duration-1000", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-foreground mb-6 tracking-tight">
            Comprehensive Navigation Services
          </h2>
          <p className="text-xl text-muted-foreground leading-relaxed">Experienced guidance tailored to your unique diagnosis. I help you interpret complex medical information, explore all viable treatment paths, and make empowered decisions aligned with your personal goals.</p>
        </div>

        {}
        <div className="flex flex-col gap-16 lg:gap-24">
          {services.map((service, index) => <ServiceBlock key={service.title} item={service} index={index} />)}
        </div>

        {}
        <div className="mt-16 lg:mt-20 flex justify-center">
          <Button asChild size="lg" className="rounded-lg px-8 py-6 text-lg h-auto group shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300">
            <Link to="/NavigationServicesPage">Explore All Services</Link>
          </Button>
        </div>

      </div>
    </section>;
}